// Firebase database service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

import { ErrorContext, withErrorHandling, withRetry } from '../utils/errorHandling';
import { firestore } from './firebase';

// Define the client types
type ClientType = 'firebase' | 'mock';

// Define the query operators
type QueryOperator =
  | '=='
  | '!='
  | '<'
  | '<='
  | '>'
  | '>='
  | 'array-contains'
  | 'in'
  | 'array-contains-any';

// Define the query condition
interface QueryCondition {
  field: string;
  operator: QueryOperator;
  value: any;
}

// Define the database service class
export class DatabaseService {
  private clientType: ClientType;
  private firestore: any;

  constructor(clientType: ClientType = 'firebase') {
    this.clientType = clientType;

    if (clientType === 'firebase') {
      this.firestore = firestore;
    }
  }

  /**
   * Get a document by ID
   */
  async get(collectionName: string, id: string): Promise<any> {
    const errorContext: ErrorContext = {
      operation: 'get',
      resource: `${collectionName}/${id}`,
    };

    return withErrorHandling(
      async () => {
        if (this.clientType === 'firebase' && this.firestore) {
          const docRef = doc(this.firestore, collectionName, id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
          } else {
            return null;
          }
        } else {
          throw new Error('No database client available');
        }
      },
      errorContext,
      null // Fallback value if operation fails
    );
  }

  /**
   * Query documents
   */
  async query(collectionName: string, conditions: QueryCondition[] = []): Promise<any[]> {
    if (this.clientType === 'firebase' && this.firestore) {
      const collectionRef = collection(this.firestore, collectionName);

      // Build the query
      let q = query(collectionRef);

      // Add conditions to the query
      if (conditions.length > 0) {
        const whereConditions = conditions.map(condition => {
          return where(condition.field, condition.operator, condition.value);
        });

        q = query(collectionRef, ...whereConditions);
      }

      const querySnapshot = await getDocs(q);
      const results: any[] = [];

      querySnapshot.forEach(doc => {
        results.push({ id: doc.id, ...doc.data() });
      });

      return results;
    } else {
      console.error('No database client available');
      return [];
    }
  }

  /**
   * Create a document
   */
  async create(collectionName: string, data: any): Promise<any> {
    const errorContext: ErrorContext = {
      operation: 'create',
      resource: collectionName,
      data: { id: data.id },
    };

    return withRetry(
      async () => {
        if (this.clientType === 'firebase' && this.firestore) {
          // If data.id exists, use it for the document reference
          // Otherwise, create a document with an auto-generated ID
          const docRef = data.id
            ? doc(this.firestore, collectionName, data.id)
            : doc(collection(this.firestore, collectionName));

          // Add the generated ID to the data object
          if (!data.id) {
            data.id = docRef.id;
          }

          await setDoc(docRef, data);
          return data;
        } else {
          throw new Error('No database client available');
        }
      },
      errorContext,
      3, // Max retries
      1000 // Delay between retries in ms
    );
  }

  /**
   * List documents with optional limit
   */
  async list(collectionName: string, limitCount?: number): Promise<any[]> {
    if (this.clientType === 'firebase' && this.firestore) {
      const collectionRef = collection(this.firestore, collectionName);
      let q = query(collectionRef);
      if (limitCount) {
        q = query(collectionRef, limit(limitCount));
      }
      const querySnapshot = await getDocs(q);
      const results: any[] = [];
      querySnapshot.forEach(doc => {
        results.push({ id: doc.id, ...doc.data() });
      });
      return results;
    } else {
      console.error('No database client available');
      return [];
    }
  }

  /**
   * Update a document
   */
  async update(collectionName: string, id: string, data: any): Promise<boolean> {
    if (this.clientType === 'firebase' && this.firestore) {
      try {
        const docRef = doc(this.firestore, collectionName, id);
        await updateDoc(docRef, data);
        return true;
      } catch (error) {
        console.error(`Error updating document in ${collectionName}:`, error);
        throw error; // Re-throw to be handled by the caller
      }
    } else {
      console.error('No database client available');
      return false;
    }
  }

  /**
   * Delete a document
   */
  async delete(collectionName: string, id: string): Promise<boolean> {
    if (this.clientType === 'firebase' && this.firestore) {
      const docRef = doc(this.firestore, collectionName, id);
      await setDoc(docRef, { deleted: true, deletedAt: new Date() });
      return true;
    } else {
      console.error('No database client available');
      return false;
    }
  }
}

// Create and export a default instance
const databaseService = new DatabaseService('firebase');
export default databaseService;
